/**
 * AppointmentPortalPage — Phase 4c++.
 *
 * Öffentliche Self-Service-Seite für Werkstatt-Kunden. Erreichbar via
 * Token-Link aus der Reminder-Mail: /appointment/<token>
 *
 * Drei Aktionen:
 * - Termin bestätigen (Customer-Confirmation)
 * - Termin verschieben (neuer Slot aus den verfügbaren)
 * - Termin stornieren
 */
import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import DealerBrandLogo from "../components/brand/DealerBrandLogo";
import PublicLegalConsent from "../components/public/PublicLegalConsent";
import { Calendar, CheckCircle2, XCircle, Clock, Car, Wrench, Building2, Phone, MapPin, Star, FileText } from "lucide-react";

import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AppointmentPortalPage() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [action, setAction] = useState(null); // confirm | reschedule | cancel
    const [busy, setBusy] = useState(false);
    const [success, setSuccess] = useState(null);
    const [chosenSlot, setChosenSlot] = useState(null);
    const [cancelReason, setCancelReason] = useState("");
    const [legalOk, setLegalOk] = useState(false);
    // Review-State
    const [reviewRating, setReviewRating] = useState(0);
    const [reviewHover, setReviewHover] = useState(0);
    const [reviewComment, setReviewComment] = useState("");
    const [reviewConsent, setReviewConsent] = useState(false);
    const [reviewSubmitting, setReviewSubmitting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const r = await axios.get(`${API}/public/appointment/${token}`);
            setData(r.data);
        } catch (e) {
            setError(e.response?.data?.detail || "Termin konnte nicht geladen werden");
        }
        setLoading(false);
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const confirm = async () => {
        setBusy(true);
        try {
            await axios.post(`${API}/public/appointment/${token}/confirm`);
            setSuccess("Vielen Dank! Wir freuen uns auf Sie.");
            setAction(null);
            load();
        } catch (e) { setError(e.response?.data?.detail || "Bestätigung fehlgeschlagen"); }
        setBusy(false);
    };

    const reschedule = async () => {
        if (!chosenSlot) return;
        setBusy(true);
        try {
            await axios.post(`${API}/public/appointment/${token}/reschedule`, {
                new_date: chosenSlot.date,
                new_time: chosenSlot.time,
            });
            setSuccess(`Termin verschoben auf ${chosenSlot.date} um ${chosenSlot.time}. Sie erhalten eine neue Erinnerung.`);
            setAction(null);
            setChosenSlot(null);
            load();
        } catch (e) { setError(e.response?.data?.detail || "Verschieben fehlgeschlagen"); }
        setBusy(false);
    };

    const cancel = async () => {
        setBusy(true);
        try {
            await axios.post(`${API}/public/appointment/${token}/cancel`, {
                reason: cancelReason || "Kunde hat online storniert",
            });
            setSuccess("Termin wurde storniert. Falls Sie einen neuen Termin möchten, kontaktieren Sie uns gern direkt.");
            setAction(null);
            setCancelReason("");
            load();
        } catch (e) { setError(e.response?.data?.detail || "Stornierung fehlgeschlagen"); }
        setBusy(false);
    };

    const submitReview = async () => {
        if (!reviewRating) return;
        setReviewSubmitting(true);
        setError(null);
        try {
            await axios.post(`${API}/public/appointment/${token}/review`, {
                rating: reviewRating,
                comment: reviewComment.trim() || null,
                consent_publish: reviewConsent,
            });
            setSuccess("Vielen Dank für Ihre Bewertung!");
            load();
        } catch (e) {
            setError(e.response?.data?.detail || "Bewertung konnte nicht gespeichert werden");
        }
        setReviewSubmitting(false);
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center bg-gray-50 text-sm text-gray-500">Lade Termin…</div>;
    }
    if (error && !data) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
                <div className="bg-white border-l-4 border-red-500 rounded-sm p-8 max-w-lg shadow-sm" data-testid="portal-error">
                    <XCircle className="w-10 h-10 text-red-500 mb-3" />
                    <div className="font-display font-black text-2xl text-[#111827]">Termin nicht gefunden</div>
                    <p className="text-sm text-gray-700 mt-2">{error}</p>
                    <p className="text-xs text-gray-500 mt-3">Möglicherweise ist der Link abgelaufen oder bereits verwendet worden.</p>
                </div>
            </div>
        );
    }

    const appointmentDt = data.appointment_at ? new Date(data.appointment_at) : null;
    const isCancelled = data.status === "cancelled";
    const isInProgress = data.status === "in_progress";
    const isCompleted = ["completed", "invoiced"].includes(data.status);

    return (
        <div className="min-h-screen bg-gray-50 py-6 md:py-12 px-4" data-testid="appointment-portal-page">
            <div className="max-w-2xl mx-auto">
                {/* Header */}
                <div className="bg-[#111827] text-white rounded-t-sm px-6 py-5">
                    <div className="mb-1">
                        <DealerBrandLogo variant="plain" logoUrl={data.dealer?.logo} companyName={data.dealer?.company}
                            imgClassName="h-8 max-w-[180px]" textClassName="text-[10px] font-bold uppercase tracking-wider text-[#FFCC00]" />
                    </div>
                    <h1 className="font-display font-black text-3xl md:text-4xl mt-1">Ihr Werkstatt-Termin</h1>
                    <div className="text-xs text-gray-300 mt-1 font-mono-num">Auftrag #{data.order_number}</div>
                </div>

                {/* Status-Banner */}
                {isCancelled && (
                    <div className="bg-red-50 border-l-4 border-red-500 px-6 py-4 text-sm" data-testid="portal-cancelled-banner">
                        <XCircle className="w-5 h-5 text-red-700 inline mr-2" />
                        <strong>Dieser Termin wurde storniert.</strong>
                    </div>
                )}
                {isInProgress && (
                    <div className="bg-blue-50 border-l-4 border-blue-500 px-6 py-4 text-sm">
                        <Wrench className="w-5 h-5 text-blue-700 inline mr-2 animate-pulse" />
                        <strong>Ihr Fahrzeug wird gerade bearbeitet.</strong>
                    </div>
                )}
                {isCompleted && (
                    <div className="bg-emerald-50 border-l-4 border-emerald-500 px-6 py-4 text-sm">
                        <CheckCircle2 className="w-5 h-5 text-emerald-700 inline mr-2" />
                        <strong>Termin abgeschlossen — vielen Dank für Ihr Vertrauen!</strong>
                    </div>
                )}
                {data.customer_confirmed && !isCancelled && (
                    <div className="bg-emerald-50 border-l-4 border-emerald-500 px-6 py-4 text-sm" data-testid="portal-confirmed-banner">
                        <CheckCircle2 className="w-5 h-5 text-emerald-700 inline mr-2" />
                        <strong>Termin bereits bestätigt.</strong> Wir freuen uns auf Sie.
                    </div>
                )}
                {success && (
                    <div className="bg-emerald-50 border-l-4 border-emerald-500 px-6 py-4 text-sm" data-testid="portal-success">
                        <CheckCircle2 className="w-5 h-5 text-emerald-700 inline mr-2" />
                        {success}
                    </div>
                )}

                {/* Termin-Details */}
                <div className="bg-white px-6 py-6 space-y-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold flex items-center gap-1.5">
                            <Calendar className="w-3 h-3" /> Termin
                        </div>
                        <div className="font-mono-num font-black text-2xl md:text-3xl text-[#111827] mt-1">
                            {appointmentDt
                                ? appointmentDt.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })
                                : "—"}
                            <span className="ml-2 text-[#FFCC00]">{appointmentDt ? appointmentDt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : ""}</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1"><Clock className="w-3 h-3 inline" /> ca. {data.duration_min} Minuten</div>
                    </div>
                    {data.vehicle && (
                        <div className="flex items-center gap-2 text-sm border-t border-gray-100 pt-3">
                            <Car className="w-4 h-4 text-gray-500" />
                            <span><strong>{data.vehicle}</strong></span>
                        </div>
                    )}
                    {data.employee_label && (
                        <div className="bg-amber-50 border-l-4 border-amber-500 px-4 py-3 text-sm" data-testid="portal-employee-info">
                            <Wrench className="w-4 h-4 text-amber-700 inline mr-1.5" />
                            <strong>{data.employee_label}</strong> kümmert sich um Ihren Termin.
                        </div>
                    )}
                    {data.complaint && (
                        <div className="text-xs text-gray-700 italic border-l-2 border-gray-300 pl-3">
                            „{data.complaint}"
                        </div>
                    )}
                    {(data.documents || []).length > 0 && (
                        <div className="pt-1" data-testid="portal-documents">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">Dokumente</div>
                            <div className="space-y-1.5">
                                {data.documents.map((d, i) => (
                                    <a key={i} href={`${API.replace('/api','')}${d.url}`} target="_blank" rel="noopener noreferrer"
                                        className="flex items-center gap-2 text-sm text-[#111827] hover:text-[#FFCC00] bg-gray-50 border border-gray-200 rounded-sm px-3 py-2"
                                        data-testid={`portal-document-${i}`}>
                                        <FileText className="w-4 h-4 shrink-0" />
                                        <span className="truncate">{d.name || (d.kind === "handover_protocol" ? "Annahmeprotokoll" : "Dokument")}</span>
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Aktionen */}
                {!isCancelled && !isCompleted && (
                    <div className="bg-gray-50 border-t border-gray-200 px-6 py-5">
                        {!action && (
                            <>
                            {!data.customer_confirmed && (
                                <PublicLegalConsent token={token} portal="appointment" onAcceptedChange={setLegalOk} accent="#111827" className="mb-3" />
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="portal-actions">
                                <button
                                    onClick={() => { setAction("confirm"); confirm(); }}
                                    disabled={busy || data.customer_confirmed || !legalOk}
                                    className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black px-4 py-3 rounded-sm text-sm uppercase tracking-wider disabled:opacity-50"
                                    data-testid="portal-btn-confirm"
                                >
                                    <CheckCircle2 className="w-4 h-4 inline mr-1.5" />
                                    {data.customer_confirmed ? "Bereits bestätigt" : "Termin bestätigen"}
                                </button>
                                <button
                                    onClick={() => setAction("reschedule")}
                                    disabled={busy || isInProgress}
                                    className="bg-white hover:bg-gray-100 text-[#111827] font-bold border border-gray-300 px-4 py-3 rounded-sm text-sm uppercase tracking-wider"
                                    data-testid="portal-btn-reschedule"
                                >
                                    <Clock className="w-4 h-4 inline mr-1.5" />
                                    Verschieben
                                </button>
                                <button
                                    onClick={() => setAction("cancel")}
                                    disabled={busy || isInProgress}
                                    className="bg-white hover:bg-red-50 text-red-700 font-bold border border-red-200 px-4 py-3 rounded-sm text-sm uppercase tracking-wider"
                                    data-testid="portal-btn-cancel"
                                >
                                    <XCircle className="w-4 h-4 inline mr-1.5" />
                                    Stornieren
                                </button>
                            </div>
                            </>
                        )}

                        {action === "reschedule" && (
                            <div data-testid="portal-reschedule-panel">
                                <div className="font-display font-black text-lg mb-2">Neuen Termin wählen</div>
                                <div className="bg-white border border-gray-200 rounded-sm p-3 max-h-72 overflow-y-auto">
                                    {(data.available_slots || []).length === 0 ? (
                                        <p className="text-sm text-gray-500 py-4 text-center">Keine freien Slots verfügbar. Bitte kontaktieren Sie uns telefonisch.</p>
                                    ) : (
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                                            {data.available_slots.map((s, idx) => {
                                                const isSelected = chosenSlot && chosenSlot.date === s.date && chosenSlot.time === s.time;
                                                return (
                                                    <button
                                                        key={idx}
                                                        onClick={() => setChosenSlot(s)}
                                                        className={`text-xs font-mono-num px-2 py-1.5 rounded-sm border ${isSelected ? "bg-[#FFCC00] text-[#111827] border-[#E6B800] font-black" : "bg-gray-50 border-gray-200 hover:bg-gray-100"}`}
                                                        data-testid={`portal-slot-${s.date}-${s.time}`}
                                                    >
                                                        {new Date(s.date + "T12:00:00Z").toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" })}
                                                        <br />{s.time}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <div className="flex gap-2 mt-3">
                                    <button onClick={() => { setAction(null); setChosenSlot(null); }} className="text-sm px-4 py-2 rounded-sm hover:bg-gray-200">Abbrechen</button>
                                    <button onClick={reschedule} disabled={!chosenSlot || busy} className="bg-[#111827] hover:bg-black text-white font-black px-4 py-2 rounded-sm text-sm uppercase tracking-wider disabled:opacity-50" data-testid="portal-submit-reschedule">
                                        Termin verschieben
                                    </button>
                                </div>
                            </div>
                        )}

                        {action === "cancel" && (
                            <div data-testid="portal-cancel-panel">
                                <div className="font-display font-black text-lg mb-2">Termin stornieren</div>
                                <p className="text-sm text-gray-700 mb-2">Wir bedauern, dass Sie nicht kommen können. Möchten Sie uns kurz mitteilen, warum?</p>
                                <textarea
                                    value={cancelReason}
                                    onChange={(e) => setCancelReason(e.target.value)}
                                    rows={3}
                                    placeholder={"Optional — z.B. „Termin kollidiert“, „Bin im Urlaub“…"}
                                    maxLength={500}
                                    className="w-full rounded-sm border border-gray-300 p-2 text-sm"
                                    data-testid="portal-cancel-reason"
                                />
                                <div className="flex gap-2 mt-3">
                                    <button onClick={() => setAction(null)} className="text-sm px-4 py-2 rounded-sm hover:bg-gray-200">Zurück</button>
                                    <button onClick={cancel} disabled={busy} className="bg-red-700 hover:bg-red-800 text-white font-black px-4 py-2 rounded-sm text-sm uppercase tracking-wider" data-testid="portal-submit-cancel">
                                        Endgültig stornieren
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Review-Widget für abgeschlossene Aufträge */}
                {isCompleted && (
                    <div className="bg-white border-t border-gray-200 px-6 py-6" data-testid="portal-review-widget">
                        {data.review_id ? (
                            <div className="text-center py-2" data-testid="portal-review-done">
                                <div className="flex justify-center gap-1 mb-2">
                                    {[1, 2, 3, 4, 5].map((s) => (
                                        <Star
                                            key={s}
                                            className={`w-7 h-7 ${s <= (data.review_rating || 0) ? "fill-[#FFCC00] text-[#FFCC00]" : "text-gray-300"}`}
                                        />
                                    ))}
                                </div>
                                <div className="text-sm font-bold text-[#111827]">Bewertung erfasst — vielen Dank!</div>
                                <div className="text-xs text-gray-500 mt-1">
                                    Ihre {data.review_rating}-Sterne-Bewertung wurde gespeichert.
                                </div>
                            </div>
                        ) : (
                            <div data-testid="portal-review-form">
                                <div className="font-display font-black text-lg text-[#111827] mb-1">
                                    Wie zufrieden waren Sie?
                                </div>
                                <p className="text-xs text-gray-600 mb-3">
                                    Ihre Rückmeldung hilft anderen Kunden und der Werkstatt, sich weiter zu verbessern.
                                </p>
                                <div className="flex gap-1.5 mb-3" role="radiogroup" aria-label="Bewertung">
                                    {[1, 2, 3, 4, 5].map((s) => (
                                        <button
                                            key={s}
                                            type="button"
                                            onClick={() => setReviewRating(s)}
                                            onMouseEnter={() => setReviewHover(s)}
                                            onMouseLeave={() => setReviewHover(0)}
                                            className="p-1 transition-transform hover:scale-110"
                                            aria-label={`${s} Stern${s > 1 ? "e" : ""}`}
                                            data-testid={`portal-review-star-${s}`}
                                        >
                                            <Star
                                                className={`w-9 h-9 ${s <= (reviewHover || reviewRating) ? "fill-[#FFCC00] text-[#FFCC00]" : "text-gray-300"}`}
                                            />
                                        </button>
                                    ))}
                                </div>
                                <textarea
                                    value={reviewComment}
                                    onChange={(e) => setReviewComment(e.target.value)}
                                    rows={3}
                                    placeholder="Ihr Kommentar (optional, max. 2000 Zeichen)"
                                    maxLength={2000}
                                    className="w-full rounded-sm border border-gray-300 p-2 text-sm mb-2"
                                    data-testid="portal-review-comment"
                                />
                                <label className="flex items-start gap-2 text-xs text-gray-700 mb-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={reviewConsent}
                                        onChange={(e) => setReviewConsent(e.target.checked)}
                                        className="mt-0.5"
                                        data-testid="portal-review-consent"
                                    />
                                    <span>
                                        Ich erlaube, dass meine Bewertung (mit Vorname) auf der öffentlichen Werkstatt-Seite
                                        angezeigt werden darf. Der Händler entscheidet zusätzlich über die Freigabe.
                                    </span>
                                </label>
                                <button
                                    onClick={submitReview}
                                    disabled={!reviewRating || reviewSubmitting}
                                    className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black px-5 py-2.5 rounded-sm text-sm uppercase tracking-wider disabled:opacity-50"
                                    data-testid="portal-submit-review"
                                >
                                    {reviewSubmitting ? "Wird gespeichert…" : "Bewertung abgeben"}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Footer: Kontakt */}
                <div className="bg-gray-100 border-t border-gray-200 rounded-b-sm px-6 py-4 text-xs text-gray-700">
                    <div className="font-bold text-[#111827] flex items-center gap-1.5">
                        <Building2 className="w-3 h-3" /> {data.dealer?.company}
                    </div>
                    {data.dealer?.address && (
                        <div className="mt-1 flex items-start gap-1.5"><MapPin className="w-3 h-3 mt-0.5" /> {data.dealer.address}</div>
                    )}
                    {data.dealer?.phone && (
                        <div className="mt-1 flex items-start gap-1.5"><Phone className="w-3 h-3 mt-0.5" /> <a href={`tel:${data.dealer.phone}`} className="hover:underline">{data.dealer.phone}</a></div>
                    )}
                </div>
            </div>
        </div>
    );
}
